import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { newsroomApi, NewsArticleStatus } from '@/services/newsroomApi';
import ArticleCard from '@/components/Newsroom/ArticleCard';

const TABS: Array<{ key: NewsArticleStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'in_review', label: 'In Review' },
  { key: 'published', label: 'Published' },
  { key: 'rejected', label: 'Rejected' },
];

type SortOrder = 'desc' | 'asc';

const NewsroomDashboard: React.FC = () => {
  const [status, setStatus] = useState<NewsArticleStatus | 'all'>('all');
  const [outlet, setOutlet] = useState('');
  const [language, setLanguage] = useState<'en' | 'ta' | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Debounce the free-text search so every keystroke doesn't fire a request
  // — only the server-side filtered/sorted result is shown (unlike
  // HistoryPanel's client-side filtering elsewhere), since articles are
  // paginated server-side and only one page is ever loaded at a time.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: sourcesData } = useQuery({
    queryKey: ['newsroom-sources'],
    queryFn: () => newsroomApi.getSources(),
  });
  const outlets = useMemo(
    () => Array.from(new Set((sourcesData ?? []).map((s) => s.outlet))).sort(),
    [sourcesData]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['newsroom-articles', status, outlet, language, search, dateFrom, dateTo, sortOrder],
    queryFn: () =>
      newsroomApi.getArticles({
        status: status === 'all' ? undefined : status,
        outlet: outlet || undefined,
        language: language || undefined,
        search: search || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo ? `${dateTo}T23:59:59.999Z` : undefined,
        sortBy: 'publishedAt',
        sortOrder,
        limit: 50,
      }),
    // Drafts are still moving through translate/fact-check in the background
    // (15-min cron drain) — poll while viewing that tab so status updates
    // without a manual refresh.
    refetchInterval: status === 'draft' || status === 'all' ? 15_000 : false,
  });

  const articles = data?.articles ?? [];
  const hasFilters = !!(outlet || language || search || dateFrom || dateTo);

  const clearFilters = () => {
    setOutlet('');
    setLanguage('');
    setSearchInput('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Newsroom</h1>
        <Link
          to="/app/newsroom/sources"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          <AdjustmentsHorizontalIcon className="h-4 w-4" />
          Manage sources
        </Link>
      </div>

      <div className="flex gap-2 mb-4 border-b border-gray-200 dark:border-gray-700">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatus(tab.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              status === tab.key
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Search</label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search headline or body…"
              className="w-full pl-8 pr-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Source</label>
          <select
            value={outlet}
            onChange={(e) => setOutlet(e.target.value)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-1.5"
          >
            <option value="">All sources</option>
            {outlets.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Language</label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as 'en' | 'ta' | '')}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-1.5"
          >
            <option value="">All languages</option>
            <option value="en">English</option>
            <option value="ta">Tamil</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-1.5"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-1.5"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Sort by date</label>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as SortOrder)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-1.5"
          >
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </div>

        {hasFilters && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 pb-1.5"
          >
            <XMarkIcon className="h-4 w-4" />
            Clear filters
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 text-sm text-gray-500 dark:text-gray-400">
          {hasFilters ? 'No articles match these filters.' : 'No articles yet. Add a news source to start ingesting.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((article) => (
            <ArticleCard key={article._id} article={article} />
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsroomDashboard;
